import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { CheckCircle, Videocam } from '@mui/icons-material';
import { RouteMap } from '@/routes/Routes';
import NewNoteModal from '@/components/CustomModal/NewNoteModal';
import { useExtensionManager } from '@/hooks/useExtensionManager';
import { useAuthStore } from '@/stores/authStore';
import { isMobileDevice } from '@/utils/deviceDetection';
import logoHorizontal from '@/assets/logo-horizontal.png';

const AUTO_OPEN_INITIAL_MS = 2000;
const AUTO_OPEN_RETRY_MS = 2500;

const LogoLink = styled(Link)({
  display: 'inline-block',
  transition: 'opacity 0.3s ease',
  '&:hover': {
    opacity: 0.8,
  },
});

const GradientButton = styled(motion.button)({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '100px',
  padding: '14px 40px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  letterSpacing: '0.3px',
});

const FirstVideoPage = () => {
  const navigate = useNavigate();
  const [extensionOpened, setExtensionOpened] = useState(false);
  const isMobile = isMobileDevice();
  const timersRef = useRef<number[]>([]);
  const autoOpenedRef = useRef(false);
  const extensionOpenedRef = useRef(false);
  const { userData } = useAuthStore();

  // useExtensionManager handles beta tester extension ID, install modal, and plan sync
  const {
    isModalOpen,
    setIsModalOpen,
    checkExtensionInstalled: checkAndOpenExtension,
    onModalConfirm,
    isExtensionInstalled,
  } = useExtensionManager();

  const addTimer = useCallback((fn: () => void, delay: number) => {
    const id = window.setTimeout(fn, delay);
    timersRef.current.push(id);
    return id;
  }, []);

  const openExtension = useCallback(() => {
    window.postMessage({ type: 'OPEN_EXTENSION' }, '*');
  }, []);

  const tryOpenExtension = useCallback(async () => {
    if (extensionOpenedRef.current) return true;
    const installed = await isExtensionInstalled();
    if (installed) {
      openExtension();
      setExtensionOpened(true);
      extensionOpenedRef.current = true;
      return true;
    }
    return false;
  }, [isExtensionInstalled, openExtension]);

  // Auto-open extension once userData is available (desktop only)
  // Waits for userData so the beta extension ID is resolved correctly
  useEffect(() => {
    if (isMobile || autoOpenedRef.current || !userData) return;
    autoOpenedRef.current = true;

    addTimer(() => {
      void tryOpenExtension();
    }, AUTO_OPEN_INITIAL_MS);

    addTimer(() => {
      void tryOpenExtension();
    }, AUTO_OPEN_RETRY_MS);
  }, [isMobile, userData, addTimer, tryOpenExtension]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(window.clearTimeout);
    };
  }, []);

  const handleStartRecording = () => {
    // checkAndOpenExtension handles beta ID, opens extension or shows install modal
    void checkAndOpenExtension();
  };

  const handleSkip = () => {
    navigate(RouteMap.HOME, { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#001C53',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Logo */}
      <Box sx={{ position: 'absolute', top: 32, left: 0, right: 0, textAlign: 'center', zIndex: 10 }}>
        <LogoLink to={RouteMap.HOME}>
          <Box component="img" src={logoHorizontal} alt="NoteGo" sx={{ width: { xs: 180, sm: 240 }, height: 'auto' }} />
        </LogoLink>
      </Box>

      {/* Card */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        style={{ zIndex: 10 }}
      >
        {isMobile ? (
          <MobileReadyCard onDashboard={handleSkip} />
        ) : (
          <ReadyCard onOpenPlugin={handleStartRecording} onSkip={handleSkip} extensionOpened={extensionOpened} />
        )}
      </motion.div>

      <NewNoteModal open={isModalOpen} handleClose={() => setIsModalOpen(false)} onConfirm={onModalConfirm} />
    </Box>
  );
};

/** Ready to record card (desktop) */
const ReadyCard = ({
  onOpenPlugin,
  onSkip,
  extensionOpened,
}: {
  onOpenPlugin: () => void;
  onSkip: () => void;
  extensionOpened: boolean;
}) => (
  <Box
    sx={{
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(20px)',
      borderRadius: '23px',
      padding: { xs: '36px 28px', sm: '48px' },
      textAlign: 'center',
      boxShadow: '0px 30px 80px rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(255, 255, 255, 0.5)',
      maxWidth: 500,
      mx: 2,
    }}
  >
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, type: 'spring', stiffness: 200, damping: 15 }}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.4)',
        }}
      >
        <Videocam sx={{ fontSize: 56, color: '#fff' }} />
      </Box>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.4 }}
    >
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          mb: 2,
          background: 'linear-gradient(135deg, #2B9FFF 0%, #667eea 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontSize: { xs: '28px', sm: '34px' },
        }}
      >
        Time to Record!
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '16px', lineHeight: 1.6, mb: 4 }}>
        Use the NoteGo panel to create your first recording.
      </Typography>
    </motion.div>

    <Stack spacing={2} alignItems="center">
      {extensionOpened ? (
        <Typography sx={{ color: 'text.secondary', fontSize: '16px', lineHeight: '48px' }}>
          Recording panel is ready!
        </Typography>
      ) : (
        <GradientButton whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={onOpenPlugin}>
          Open Plugin
        </GradientButton>
      )}
      <Typography
        component="button"
        onClick={onSkip}
        sx={{
          background: 'none',
          border: 'none',
          color: 'text.secondary',
          fontSize: '14px',
          cursor: 'pointer',
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
          '&:hover': { color: 'text.primary' },
          p: 0,
        }}
      >
        Skip to Dashboard
      </Typography>
    </Stack>
  </Box>
);

/** Mobile variant (no extension) */
const MobileReadyCard = ({ onDashboard }: { onDashboard: () => void }) => (
  <Box
    sx={{
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(20px)',
      borderRadius: '23px',
      padding: { xs: '36px 28px', sm: '48px' },
      textAlign: 'center',
      boxShadow: '0px 30px 80px rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(255, 255, 255, 0.5)',
      maxWidth: 500,
      mx: 2,
    }}
  >
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, type: 'spring', stiffness: 200, damping: 15 }}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.4)',
        }}
      >
        <CheckCircle sx={{ fontSize: 56, color: '#fff' }} />
      </Box>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.4 }}
    >
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          mb: 2,
          background: 'linear-gradient(135deg, #2B9FFF 0%, #667eea 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontSize: { xs: '28px', sm: '34px' },
        }}
      >
        Head to Your Dashboard
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '16px', lineHeight: 1.6, mb: 4 }}>
        Extensions are available on Chrome desktop. Visit your dashboard to explore NoteGo.
      </Typography>
    </motion.div>

    <GradientButton whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={onDashboard}>
      Go to Dashboard
    </GradientButton>
  </Box>
);

export default FirstVideoPage;
